import { useAuthStore } from '@/Stores/auth-v2-store';
import {
  deriveStripeBannerState,
  type StripeBannerState,
} from '@/features/societies/helpers/stripe-banner-state';
import { useMySocietiesStripe, type MySocietyStripe } from './useMySocietiesStripe';

export interface ExternalStripeBanner {
  society: MySocietyStripe;
  state: StripeBannerState;
}

/**
 * Bandeaux Stripe à afficher à un vendeur (utilisateur EXTERNAL) : un par
 * société attachée présentant un problème Stripe.
 *
 *  - Rien sans la permission `stripe.account`.
 *  - Skip des sociétés facturées par un parent (le parent porte le compte).
 *  - Dédup par `stripeAccountId` (plusieurs sociétés partageant un compte).
 *
 * Source unique de la règle « ai-je un bandeau Stripe ? » : le composant
 * `StripeOnboardingBanner` l'affiche, les nouveautés s'en servent pour ne pas
 * s'afficher par-dessus (`isResolved` leur dit quand la réponse est connue).
 */
export function useExternalStripeBanners(): {
  banners: ExternalStripeBanner[];
  /**
   * `true` quand on SAIT s'il y a un bandeau : pas de permission (donc aucun),
   * ou état Stripe des sociétés chargé (ou en échec définitif — aucun bandeau
   * n'est alors affiché). `false` pendant le chargement, et pour un utilisateur
   * qui n'est pas EXTERNAL (ce hook ne le concerne pas).
   */
  isResolved: boolean;
} {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { societies, isSettled, isExternalUser } = useMySocietiesStripe();

  if (!hasPermission('stripe.account')) {
    return { banners: [], isResolved: isExternalUser };
  }

  const seenAccounts = new Set<string>();
  const banners = societies
    // Filtre « enfant facturé par un parent » → bandeau géré côté parent.
    .filter((s) => !(s.billedId && s.billedId !== s.societyId))
    .map((society) => ({
      society,
      state: deriveStripeBannerState(society, society.monthlyPayment ?? undefined),
    }))
    .filter((b): b is ExternalStripeBanner => b.state !== null)
    // Dédup par compte Stripe (évite un doublon si 2 sociétés partagent un compte).
    .filter((b) => {
      const acct = b.society.stripeAccountId;
      if (!acct) return true;
      if (seenAccounts.has(acct)) return false;
      seenAccounts.add(acct);
      return true;
    });

  return { banners, isResolved: isSettled };
}
